import React, { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import ErrorMessage from '../../components/common/ErrorMessage.jsx';
import useReporting from '../../hooks/useReporting.js';
import StatTable from '../../components/reporting/StatTable.jsx';
import { setActiveTab } from '../../redux/slices/header-slice.js';
import ReportingSubLinks from '../../components/header/ReportingSubLinks.jsx';
import BackArrow from '../../components/common/BackArrow.jsx';

/**
 * Converts tables from service to StatTable-compatible rows
 * Ensures each cell is a primitive for display
 */
const prepareRowsForStatTable = (tables) => {
    return tables.map((table) => ({
        ...table,
        rows: table.rows.map((row) =>
            row.map((cell) => ({
                id: cell.id || uuidv4(),
                data: cell.data?.display ?? cell.data ?? '-' // always primitive for UI
            }))
        )
    }));
};

/**
 * Component for rendering weekly statistics page.
 * @param {Object} props - Component props.
 * @param {string} props.title - Title of the page.
 * @param {string} props.itSegment - IT segment for which statistics are displayed.
 * @returns {JSX.Element} WeeklyStatsPage component.
 */
const WeeklyStatsPage = ({ title, itSegment }) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setActiveTab('Reporting'));

        return () => {
            dispatch(setActiveTab(null));
        };
    });

    const { loading, error, tables, getWeeklyStats } = useReporting();

    const endDate = new Date();
    endDate.setUTCDate(
        endDate.getUTCDate() - ((endDate.getUTCDay() + 6) % 7) - 1
    );
    const startDate = new Date(endDate);
    startDate.setUTCDate(startDate.getUTCDate() - (startDate.getUTCDay() + 6));

    useEffect(() => {
        const fetchData = async () => {
            await getWeeklyStats(itSegment);
        };

        fetchData();
    }, [itSegment]);

    // Prepare rows for StatTable
    const preparedTables = tables ? prepareRowsForStatTable(tables) : [];

    return (
        <div
            style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
            {/* Top Links */}
            <ReportingSubLinks />
            <div
                className="newsc-container"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflow: 'hidden'
                }}>
                {/* Sticky Header */}
                <div
                    style={{
                        background: '#ffffff',
                        padding: '20px 0',
                        marginTop: '60px',
                        borderBottom: '1px solid #e5e7eb',
                        zIndex: 5
                    }}>
                    <div className="create-office-heading">
                        <BackArrow navigateTo={-1} />
                        <h2 style={{ margin: 0 }}>{`${title} ${
                            startDate.getUTCMonth() + 1
                        }/${startDate.getDate()}/${startDate.getUTCFullYear()}-${
                            endDate.getUTCMonth() + 1
                        }/${endDate.getDate()}/${endDate.getUTCFullYear()}`}</h2>
                    </div>
                </div>
                {/* Scrollable Content */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        paddingTop: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                    {loading && <LoadingSpinner />}
                    {error && <ErrorMessage message={error.message} />}
                    {preparedTables.length > 0 &&
                        preparedTables.map((table) => (
                            <StatTable
                                title={table.title}
                                key={uuidv4()}
                                rows={table.rows}
                                columnHeaders={table.headers}
                            />
                        ))}
                </div>
            </div>
        </div>
    );
};

WeeklyStatsPage.propTypes = {
    title: PropTypes.string,
    itSegment: PropTypes.string.isRequired
};

WeeklyStatsPage.defaultProps = {
    title: ''
};

export default WeeklyStatsPage;
